/* Missing PoDs grouped by days since delivery, as one bar per group (length = share of all pending).
   Click a group to list it in the shipments table; the icon downloads it; "Download all" gets every pending one. */
import React from 'react'
import { Box, ButtonBase, Card, CardContent, IconButton, Tooltip, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { TONE, bucketTone, num } from './podUtils'
import { CountUp, enter, growX } from './podMotion'

export default function PodAgingStrip({ labels = [], counts = [], selected, onSelect, onDownload, onDownloadAll, downloading }) {
  const total = counts.reduce((s, n) => s + (n || 0), 0)
  const max = Math.max(1, ...counts)
  return (
    <Card elevation={0} sx={{ border: 1, borderColor: 'divider', height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
          <Box>
            <Typography component='h2' sx={{ fontWeight: 700, fontSize: 16 }}>Pending PoDs by age</Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Days since delivery · {num(total)} pending</Typography>
          </Box>
          <LoadingButton size='small' variant='outlined' startIcon={<FileDownloadOutlinedIcon />} loading={downloading} disabled={!total}
            onClick={onDownloadAll} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>Download all</LoadingButton>
        </Box>
        <Box component='ul' sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.5 }}>
          {labels.map((label, i) => {
            const count = counts[i] || 0
            const tone = TONE[count ? bucketTone(i, labels.length) : 'neutral']
            const isSel = selected === i
            const share = total ? Math.round((100 * count) / total) : 0
            return (
              <Box component='li' key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ButtonBase onClick={() => onSelect(isSel ? null : i)} disabled={!count} aria-pressed={isSel}
                  aria-label={`${label}: ${count} pending. ${isSel ? 'Showing these' : 'Show these'}`}
                  sx={{ flex: 1, display: 'grid', gridTemplateColumns: '96px 1fr 64px', alignItems: 'center', gap: 1.25, textAlign: 'left',
                    borderRadius: 1.5, px: 1, py: 0.75, minHeight: 44, border: 1, borderColor: isSel ? 'primary.main' : 'transparent',
                    bgcolor: isSel ? 'action.selected' : 'transparent', '&:hover': { bgcolor: 'action.hover' },
                    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' } }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{label}</Typography>
                  <Box sx={{ height: 12, borderRadius: 1, bgcolor: 'grey.200', overflow: 'hidden' }} aria-hidden>
                    {/* grows in on load (staggered by group), then eases to new lengths when filters change */}
                    <Box sx={{ height: '100%', width: `${(100 * count) / max}%`, bgcolor: tone.fg, borderRadius: 1, transformOrigin: 'left center',
                      transition: 'width .6s cubic-bezier(.2,.8,.2,1)', ...enter(growX, 800, 150 + i * 120),
                      '@media (prefers-reduced-motion: reduce)': { transition: 'none', animation: 'none' } }} />
                  </Box>
                  <Typography sx={{ fontSize: 13, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    <b><CountUp value={count} /></b> <Box component='span' sx={{ color: 'text.secondary', fontSize: 12 }}>{share}%</Box>
                  </Typography>
                </ButtonBase>
                <Tooltip title={count ? `Download ${label.toLowerCase()} (CSV)` : ''}>
                  <span>
                    <IconButton size='small' onClick={() => onDownload(i)} disabled={!count} aria-label={`Download ${label}`}>
                      <FileDownloadOutlinedIcon fontSize='small' />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            )
          })}
        </Box>
      </CardContent>
    </Card>
  )
}
